import { Icon } from "@iconify/react";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useId, useMemo, useState } from "react";
import { useTranslations } from "use-intl";
import { getAppwriteClient } from "$/modules/appwrite/lib/appwrite";
import {
	AssetPosition,
	type LiveQuestionResult,
	type LiveSession,
} from "$/modules/appwrite/types/collections";
import {
	getLiveAnswers,
	getLiveQuestionPlan,
	getLiveQuestionResults,
	getLiveSession,
	getLiveTeam,
	saveLiveRoundAnswer,
	submitLiveAnswer,
} from "$/modules/projects/lib/live-sessions";
import { getProjectById } from "$/modules/projects/lib/projects";
import { QuestionMedia } from "$/modules/question/ui/question-media";
import { Button } from "$/modules/shared/ui/button";
import {
	Card,
	CardContent,
	CardFooter,
	CardHeader,
} from "$/modules/shared/ui/card";
import { Field, FieldLabel } from "$/modules/shared/ui/field";
import { Input } from "$/modules/shared/ui/input";

export const Route = createFileRoute("/$locale/live/$sessionId/$teamId/")({
	loader: async ({ params }) => {
		const [session, team] = await Promise.all([
			getLiveSession(params.sessionId),
			getLiveTeam(params.teamId),
		]);
		if (team.sessionId !== session.$id)
			throw new Error("Invalid live team link.");
		const project = await getProjectById({ projectId: session.projectId });
		return { session, team, project };
	},
	component: LiveTeamPage,
});

function LiveTeamPage() {
	const fieldId = useId();

	const t = useTranslations("live");
	const {
		session: initialSession,
		team: initialTeam,
		project,
	} = Route.useLoaderData();
	const [team, setTeam] = useState(initialTeam);
	const [session, setSession] = useState(initialSession);
	const [answer, setAnswer] = useState<string[]>([]);
	const [textAnswer, setTextAnswer] = useState("");
	const [sent, setSent] = useState(false);
	const questions = useMemo(
		() =>
			project.questionnaires?.flatMap(
				(questionnaire) => questionnaire.questions ?? [],
			) ?? [],
		[project],
	);
	const plan = getLiveQuestionPlan(session);
	const question = session.questionPlanJson
		? plan.find((item) => item.questionId === session.currentQuestionId)
				?.question
		: questions[session.currentQuestionIndex];
	const [results, setResults] = useState<LiveQuestionResult[]>([]);
	const correctAnswersVisible = session.questionPlanJson
		? results.some((result) => result.questionId === session.currentQuestionId)
		: session.correctAnswersVisible;
	const [sendPending, setSendPending] = useState(false);
	const [sendError, setSendError] = useState("");

	useEffect(() => {
		const appwrite = getAppwriteClient();
		return appwrite.subscribe(
			`tablesdb.${session.$databaseId}.tables.liveSessions.rows.${session.$id}`,
			(event) => setSession(event.payload as LiveSession),
		);
	}, [session.$databaseId, session.$id]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: the selection must reset whenever the host advances the question.
	useEffect(() => {
		setAnswer([]);
		setTextAnswer("");
		setSent(false);
	}, [session.currentQuestionIndex, session.currentQuestionId]);
	useEffect(() => {
		if (!question || !session.questionPlanJson) return;
		let active = true;
		void getLiveAnswers(session.$id)
			.then((result) => {
				const existing = result.rows.find(
					(answer) =>
						answer.questionId === question.$id && answer.teamId === team.$id,
				);
				if (active && existing) {
					setSent(true);
					setAnswer(existing.answers);
					setTextAnswer(existing.answers[0] ?? "");
				}
			})
			.catch(() => {});
		return () => {
			active = false;
		};
	}, [
		question?.$id,
		session.$id,
		session.questionPlanJson,
		session.participationMode,
		team.$id,
	]);
	useEffect(() => {
		let active = true;
		const refresh = async () => {
			const [next, finalResults, nextTeam] = await Promise.all([
				getLiveSession(initialSession.$id),
				initialSession.questionPlanJson
					? getLiveQuestionResults(initialSession.$id)
					: Promise.resolve([]),
				getLiveTeam(initialTeam.$id),
			]);
			if (active) {
				setSession(next);
				setResults(finalResults);
				setTeam(nextTeam);
			}
		};
		void refresh().catch(() => {});
		const unsubscribe = initialSession.questionPlanJson
			? getAppwriteClient().subscribe(
					`tablesdb.${initialSession.$databaseId}.tables.liveQuestionResults.rows`,
					() => {
						void refresh().catch(() => {});
					},
				)
			: () => {};
		const timer = window.setInterval(() => {
			void refresh().catch(() => {});
		}, 5000);
		return () => {
			active = false;
			window.clearInterval(timer);
			unsubscribe();
		};
	}, [
		initialTeam.$id,
		initialSession.$id,
		initialSession.$databaseId,
		initialSession.questionPlanJson,
	]);

	if (session.participationMode === "screen")
		return (
			<LiveShell
				team={team.name}
				title={t("screenMode")}
				description={t("screenDescription")}
			/>
		);
	if (session.questionPlanJson && session.status !== "finished" && !question)
		return (
			<LiveShell
				team={team.name}
				title={t("chooseQuestion")}
				description={t("waitForSelection")}
			/>
		);
	if (session.status === "lobby") {
		return (
			<LiveShell
				team={`${team.emoji ? `${team.emoji} ` : ""}${team.name}`}
				title={t("readyLobby")}
				description={t("waitForHost")}
			/>
		);
	}
	if (session.status === "finished" || !question) {
		return (
			<LiveShell
				team={`${team.emoji ? `${team.emoji} ` : ""}${team.name}`}
				title={t("roundFinished")}
				description={t("thanks")}
			/>
		);
	}

	if (session.questionPlanJson && question.questionType === "Info")
		return (
			<LiveShell
				team={team.name}
				title={question.question}
				description={
					correctAnswersVisible
						? (question.explanation ?? "")
						: t("infoNoPoints")
				}
			/>
		);
	const isText = question.questionType === "Text";
	const toggleAnswer = (option: string) => {
		setAnswer((current) =>
			question.questionType === "Multiple"
				? current.includes(option)
					? current.filter((value) => value !== option)
					: [...current, option]
				: [option],
		);
	};
	const send = async () => {
		if (sendPending || sent || correctAnswersVisible) return;
		const answers = isText ? [textAnswer.trim()] : answer;
		if (!answers[0]) return;
		setSendPending(true);
		setSendError("");
		try {
			if (session.questionPlanJson)
				await saveLiveRoundAnswer(session.$id, question.$id, team.$id, answers);
			else await submitLiveAnswer(session.$id, team.$id, question.$id, answers);
			setSent(true);
		} catch (error) {
			setSendError(error instanceof Error ? error.message : t("loadFailed"));
		} finally {
			setSendPending(false);
		}
	};

	return (
		<main className="min-h-screen bg-content2 p-4 sm:p-8">
			<section className="mx-auto max-w-2xl">
				<p
					className="mb-3 inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-semibold"
					style={{
						backgroundColor: `${team.color ?? "#7c3aed"}1f`,
						color: team.color ?? undefined,
					}}
				>
					{team.emoji && <span className="text-base">{team.emoji}</span>}
					{team.name}
				</p>
				<Card className="overflow-hidden border border-default-200">
					<CardHeader className="border-b border-default-100 bg-content1 px-6 py-5">
						<p className="text-sm font-medium text-foreground-500">
							{t("roundProgress", {
								current: session.currentQuestionIndex + 1,
								total: questions.length,
							})}
						</p>
					</CardHeader>
					<CardContent className="gap-5 p-6 sm:p-8">
						<QuestionMedia
							question={question}
							position={AssetPosition.BeforeQuestion}
						/>
						<h1 className="font-display text-2xl font-bold leading-tight sm:text-3xl">
							{question.question}
						</h1>
						<QuestionMedia
							question={question}
							position={AssetPosition.AfterQuestion}
						/>
						{isText ? (
							<Field
								data-disabled={sent || sendPending || correctAnswersVisible}
							>
								<FieldLabel htmlFor={`${fieldId}-100`}>
									{t("teamAnswer")}
								</FieldLabel>
								<Input
									id={`${fieldId}-100`}
									value={textAnswer}
									disabled={sent || sendPending || correctAnswersVisible}
									onChange={(event) => setTextAnswer(event.currentTarget.value)}
								/>
							</Field>
						) : (
							<div className="grid gap-3 sm:grid-cols-2">
								{question.options.map((option, index) => {
									const selected = answer.includes(option);
									return (
										<Button
											key={option}
											className="h-auto min-h-14 justify-start whitespace-normal px-4 py-3 text-left"
											onClick={() => toggleAnswer(option)}
											disabled={sent || sendPending || correctAnswersVisible}
											variant={selected ? "default" : "muted"}
											type="button"
										>
											<span className="mr-3 grid size-6 shrink-0 place-items-center rounded-full bg-background/30 text-xs font-bold">
												{index + 1}
											</span>
											{option}
										</Button>
									);
								})}
							</div>
						)}
					</CardContent>
					{sendError && (
						<p role="alert" className="px-6 text-danger">
							{sendError}
						</p>
					)}
					{session.questionPlanJson && correctAnswersVisible && (
						<div className="mx-6 mb-4 rounded-xl bg-success-50 p-4">
							<p className="font-bold">{question.correctOption.join(" · ")}</p>
							<p className="mt-2 whitespace-pre-wrap">{question.explanation}</p>
						</div>
					)}
					<CardFooter className="justify-end border-t border-default-100 p-5">
						<Button
							onClick={send}
							disabled={
								sent ||
								sendPending ||
								correctAnswersVisible ||
								(isText ? !textAnswer.trim() : answer.length === 0)
							}
							variant="default"
							type="button"
						>
							{sent ? t("answerSent") : t("sendAnswer")}
							{<Icon icon={sent ? "lucide:check" : "lucide:send"} />}
						</Button>
					</CardFooter>
				</Card>
			</section>
		</main>
	);
}

function LiveShell({
	team,
	title,
	description,
}: {
	team: string;
	title: string;
	description: string;
}) {
	return (
		<main className="grid min-h-screen place-items-center bg-content2 p-4">
			<Card className="w-full max-w-md border border-default-200 text-center">
				<CardContent className="gap-3 p-8">
					<p className="text-sm font-semibold text-primary">{team}</p>
					<h1 className="font-display text-3xl font-bold">{title}</h1>
					<p className="text-foreground-500">{description}</p>
				</CardContent>
			</Card>
		</main>
	);
}
